import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Textarea } from "@/components/ui/Primitives";
import { ScrollText, Plus, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const PROVINCES = ["AB","BC","MB","NB","NL","NS","ON","PE","QC","SK","NT","NU","YT"];

export default function Notices() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({
    notice_type: "", province: "ON", landlord_name: "",
    tenant_name: "", lease_id: "", effective_date: "",
    rent_amount: "", reason: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const [n, t, p, l] = await Promise.all([
        api.get("/notices"),
        api.get("/notices/types").catch(() => ({ data: [] })),
        api.get("/properties"),
        api.get("/leases"),
      ]);
      setItems(Array.isArray(n.data) ? n.data : (n.data?.notices || []));
      setTypes(Array.isArray(t.data) ? t.data : (t.data?.types || []));
      setProperties(Array.isArray(p.data) ? p.data : (p.data?.properties || []));
      setLeases(Array.isArray(l.data) ? l.data : (l.data?.leases || []));
      if (!form.notice_type && (Array.isArray(t.data) ? t.data[0] : t.data?.types?.[0])) {
        const list = Array.isArray(t.data) ? t.data : t.data?.types;
        const first = list?.[0];
        if (first) setForm((f) => ({ ...f, notice_type: first.code || first.id || first }));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/notices/generate", {
        notice_type: form.notice_type,
        province: form.province,
        landlord_name: form.landlord_name.trim(),
        tenant_name: form.tenant_name || null,
        lease_id: form.lease_id || null,
        effective_date: form.effective_date || null,
        rent_amount_cents: form.rent_amount ? Math.round(Number(form.rent_amount) * 100) : null,
        reason: form.reason || null,
      });
      toast.success("Notice generated");
      setOpen(false);
      setViewing(data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate notice");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Notices"
        subtitle="Province-specific landlord notices, generated and stored."
        actions={<Button onClick={() => setOpen(true)} data-testid="generate-notice-button"><Plus className="w-4 h-4"/> Generate notice</Button>}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No notices generated yet"
          description="Generate formal landlord notices (rent increase, N4, N12, etc.) compliant with your province."
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((n) => (
            <Card key={n.notice_id} className="p-5 card-lift cursor-pointer" onClick={() => setViewing(n)} data-testid={`notice-card-${n.notice_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-lg font-bold text-slate-900">{n.notice_type || "Notice"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{n.tenant_name || "—"} · {n.province}</div>
                </div>
                <Pill tone="blue">{n.province}</Pill>
              </div>
              <div className="mt-3 text-xs text-slate-500">Generated {formatDate(n.created_at)}</div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Generate notice" testid="notice-modal">
        <form onSubmit={submit} className="space-y-4">
          {types.length > 0 ? (
            <Select label="Notice type" required value={form.notice_type} onChange={(e)=>setForm({...form, notice_type:e.target.value})} testid="notice-type-select">
              {types.map((t) => {
                const code = t.code || t.id || t;
                const label = t.label || t.name || code;
                return <option key={code} value={code}>{label}</option>;
              })}
            </Select>
          ) : (
            <Input label="Notice type code" required value={form.notice_type} onChange={(e)=>setForm({...form, notice_type:e.target.value})} placeholder="N4, N12, rent_increase"/>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Select label="Province" value={form.province} onChange={(e)=>setForm({...form, province:e.target.value})}>
              {PROVINCES.map((p) => <option key={p}>{p}</option>)}
            </Select>
            <Input label="Effective date" type="date" value={form.effective_date} onChange={(e)=>setForm({...form, effective_date:e.target.value})}/>
          </div>
          <Input label="Landlord name" required value={form.landlord_name} onChange={(e)=>setForm({...form, landlord_name:e.target.value})} testid="notice-landlord-input"/>
          <Input label="Tenant name (optional)" value={form.tenant_name} onChange={(e)=>setForm({...form, tenant_name:e.target.value})}/>
          {leases.length > 0 && (
            <Select label="Linked lease (optional)" value={form.lease_id} onChange={(e)=>setForm({...form, lease_id:e.target.value})}>
              <option value="">— None —</option>
              {leases.map((l) => <option key={l.lease_id} value={l.lease_id}>{l.tenant_email}</option>)}
            </Select>
          )}
          <Input label="Rent amount (if applicable, CAD)" type="number" step="0.01" value={form.rent_amount} onChange={(e)=>setForm({...form, rent_amount:e.target.value})}/>
          <Textarea label="Reason / details" value={form.reason} onChange={(e)=>setForm({...form, reason:e.target.value})}/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="notice-generate-submit">{saving ? "Generating…" : "Generate"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.notice_type || "Notice"} testid="notice-view-modal">
        {viewing && (
          <div>
            <div className="text-xs text-slate-500 mb-3">Generated {formatDate(viewing.created_at)} · {viewing.province}</div>
            <div className="bg-slate-50 rounded-lg p-5 max-h-[480px] overflow-y-auto whitespace-pre-wrap text-sm text-slate-700 leading-relaxed border border-slate-200 font-mono">
              {viewing.body || viewing.content || viewing.text || "(No content)"}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
